// A deliberately narrow consumer of arrangement timelines: an explicit
// generated-comp Beat 2 pattern. Other channels and generator settings are
// not implemented here and never alter the live band.
import { fractionToNumber, measureQuarters } from '@/music/score/duration';
import type { PerformanceMeasureOccurrence } from '@/music/score/partPlayback';
import type { FormMeasure } from '@/music/score/types';

export interface CompPatternRange {
  fromBeat: number;
  toBeat: number;
}

interface WrittenMeasure {
  id: string;
  start: number;
  length: number;
}

interface WrittenPattern {
  measures: WrittenMeasure[];
  ranges: CompPatternRange[];
}

const EPSILON = 1e-9;
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

function isBeatTwo(source: unknown): boolean {
  return (
    record(source) &&
    source.kind === 'generated' &&
    record(source.config) &&
    source.config.style === 'beat-two'
  );
}

/** Resolve this arrangement's supported comp pattern in WRITTEN order.
 * A passage may begin after the opening change, so projecting only the
 * encountered anchors would lose the pattern. The written regions are then
 * visited afresh for each performed occurrence, including returns/cycles.
 * Unsupported pass scopes or missing comp anchors make this optional default
 * unavailable; they must not silently broaden the range. */
function writtenPattern(timeline: unknown, form: readonly FormMeasure[]): WrittenPattern {
  const empty: WrittenPattern = { measures: [], ranges: [] };
  if (!record(timeline) || !record(timeline.base) || !Array.isArray(timeline.changes)) return empty;
  let meter: [number, number] = [4, 4];
  let end = 0;
  const measures = form.map((measure) => {
    meter = measure.timeSig ?? meter;
    const length =
      measure.pickupBeats == null
        ? fractionToNumber(measureQuarters(meter))
        : (measure.pickupBeats * 4) / meter[1];
    const result = { id: measure.id, start: end, length };
    end += length;
    return result;
  });
  if (measures.some((measure) => !Number.isFinite(measure.length) || measure.length <= 0))
    return empty;
  const byId = new Map(measures.map((measure) => [measure.id, measure]));
  const changes: { at: number; active: boolean; order: number }[] = [];
  for (const [order, change] of timeline.changes.entries()) {
    if (!record(change) || !record(change.patch) || !('comp' in change.patch)) continue;
    if (change.scope !== undefined && change.scope !== 'all') return empty;
    if (!record(change.at) || typeof change.at.measureId !== 'string') return empty;
    const measure = byId.get(change.at.measureId);
    if (!measure) return empty;
    let offset = 0;
    if (change.at.offset !== undefined) {
      const fraction = change.at.offset;
      if (
        !record(fraction) ||
        typeof fraction.num !== 'number' ||
        typeof fraction.den !== 'number' ||
        !Number.isSafeInteger(fraction.num) ||
        !Number.isSafeInteger(fraction.den) ||
        fraction.num < 0 ||
        fraction.den <= 0
      )
        return empty;
      offset = fraction.num / fraction.den;
      if (offset >= measure.length) return empty;
    }
    changes.push({ at: measure.start + offset, active: isBeatTwo(change.patch.comp), order });
  }
  changes.sort((a, b) => a.at - b.at || a.order - b.order);
  let active = isBeatTwo(timeline.base.comp);
  let fromBeat = 0;
  const ranges: CompPatternRange[] = [];
  for (const change of changes) {
    if (change.active === active) continue;
    if (active && change.at > fromBeat + EPSILON) ranges.push({ fromBeat, toBeat: change.at });
    active = change.active;
    fromBeat = change.at;
  }
  if (active && end > fromBeat + EPSILON) ranges.push({ fromBeat, toBeat: end });
  return { measures, ranges };
}

export function hasCompPattern(timeline: unknown, form: readonly FormMeasure[]): boolean {
  return writtenPattern(timeline, form).ranges.length > 0;
}

/** Project authored comp regions into the player's own beat clock.
 * This never creates a pattern from harmony, titles, section names or bass
 * notes. A repeated/cycled bar and a passage starting inside the region all
 * consult the same explicit written arrangement. */
export function compPatternRanges(
  timeline: unknown,
  form: readonly FormMeasure[],
  occurrences: readonly PerformanceMeasureOccurrence[],
): CompPatternRange[] {
  const written = writtenPattern(timeline, form);
  const projected: CompPatternRange[] = [];
  for (const occurrence of occurrences) {
    const measure = written.measures[occurrence.formMeasureIndex];
    if (
      !measure ||
      !Number.isFinite(occurrence.startBeat) ||
      !Number.isFinite(occurrence.beats) ||
      occurrence.beats <= 0
    )
      continue;
    for (const range of written.ranges) {
      const start = Math.max(range.fromBeat, measure.start);
      const end = Math.min(range.toBeat, measure.start + measure.length);
      if (end <= start + EPSILON) continue;
      const scale = occurrence.beats / measure.length;
      projected.push({
        fromBeat: occurrence.startBeat + (start - measure.start) * scale,
        toBeat: occurrence.startBeat + (end - measure.start) * scale,
      });
    }
  }
  projected.sort((a, b) => a.fromBeat - b.fromBeat);
  const merged: CompPatternRange[] = [];
  for (const range of projected) {
    const previous = merged[merged.length - 1];
    if (previous && range.fromBeat <= previous.toBeat + EPSILON) {
      previous.toBeat = Math.max(previous.toBeat, range.toBeat);
    } else merged.push({ ...range });
  }
  return merged;
}
